import React, { useState, useEffect } from "react";
import { FIND_SWITCH_PROMPTS } from "../../lib/content";
import { track } from "../../lib/analytics";
import { Eyebrow } from "./kit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITUATION_HREF = {
  "Growth Has Stalled": "/what-we-solve/growth-has-stalled",
  "Ready to Scale": "/what-we-solve/ready-to-scale",
  "Reposition & Transform": "/what-we-solve/reposition-or-transform",
  "Create Enterprise Value": "/what-we-solve/create-enterprise-value",
};

const PLACEHOLDERS = [
  "Tell us what is not moving, what has changed, or what you are trying to achieve.",
  "Growth has plateaued despite higher marketing spend…",
  "We are expanding, but our margins keep falling…",
  "Our teams are busy, but not aligned around one agenda…",
  "The business is still too dependent on the founder…",
  "We know where we want to go, but not how to get there…",
];

const FindYourSwitch = ({ onOpenDiagnostic, bare = false }) => {
  const [query, setQuery] = useState("");
  const [phIdx, setPhIdx] = useState(0);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [submitted, setSubmitted] = useState("");

  useEffect(() => {
    const id = setInterval(() => setPhIdx((i) => (i + 1) % PLACEHOLDERS.length), 3200);
    return () => clearInterval(id);
  }, []);

  const submit = async (text) => {
    const value = (text ?? query).trim();
    if (!value) { setError("Tell us a little about what is happening before we begin."); return; }
    if (value.length < 12) { setError("Add a little more context so we can point you in the right direction."); return; }
    setError(""); setSubmitted(value); setStatus("loading"); track("find_switch_submitted");
    try {
      const res = await fetch(`${API}/find-your-switch`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ query: value }) });
      if (!res.ok) throw new Error("bad status");
      const data = await res.json();
      setResult(data); setStatus("done"); track("find_switch_result", { situation: data.relevant_situation });
    } catch (e) { setStatus("error"); }
  };
  const reset = () => { setStatus("idle"); setResult(null); setError(""); setSubmitted(""); };

  const inner = (
    <div className="grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-4">
        <div data-testid="switchrouter-mark" className="inline-flex items-baseline">
          <span className="font-display text-3xl lowercase leading-none tracking-tight text-white md:text-4xl">switch</span>
          <span className="font-display text-3xl lowercase leading-none tracking-tight text-[#5f8f10] md:text-4xl">router</span>
        </div>
        <p className="font-body mt-3 text-xs font-semibold uppercase tracking-[0.2em] text-white/55">Your AI growth router</p>
        <h2 className="font-display mt-6 text-4xl leading-[0.95] tracking-tight text-white md:text-5xl">
          Not sure what<br />needs to<br /><span className="text-[#5f8f10]">change?</span>
        </h2>
        <p className="font-body mt-8 max-w-sm text-lg font-normal leading-relaxed text-white/90">
          Describe what is happening in your business. We&rsquo;ll identify the most relevant growth situation and point you to useful SWITCH thinking.
        </p>
      </div>

      <div className="lg:col-span-8">
        <div className="border-2 border-white/30 p-6 md:p-8">
          {status !== "done" && status !== "error" && (
            <>
              <label htmlFor="fys-input" className="font-body mb-3 block text-sm font-semibold uppercase tracking-[0.12em] text-white">
                What is happening in your business?
              </label>
              <textarea
                id="fys-input" data-testid="fys-input" value={query}
                onChange={(e) => setQuery(e.target.value)} onFocus={() => track("find_switch_started")} rows={4}
                placeholder={PLACEHOLDERS[phIdx]}
                className="font-body w-full resize-none border-2 border-white/30 bg-transparent px-4 py-3.5 text-lg font-normal text-white outline-none transition-colors placeholder:text-white/55 focus:border-[#5f8f10]"
              />
              {error && <p data-testid="fys-error" className="font-body mt-3 text-base font-semibold text-[#5f8f10]">{error}</p>}
              <div className="mt-4 flex flex-wrap gap-2">
                {FIND_SWITCH_PROMPTS.map((p) => (
                  <button key={p} type="button" data-testid="fys-prompt-chip" onClick={() => { setQuery(p); track("find_switch_prompt_selected", { prompt: p }); }} className="font-body border-2 border-white/35 px-4 py-2.5 text-sm font-medium text-white transition-colors duration-150 hover:border-[#5f8f10] hover:text-[#5f8f10]">
                    {p}
                  </button>
                ))}
              </div>
              <button type="button" data-testid="fys-submit" disabled={status === "loading"} onClick={() => submit()} className="font-body mt-6 inline-flex items-center gap-3 border-2 border-[#5f8f10] bg-[#5f8f10] px-8 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-black transition-colors duration-150 hover:border-white hover:bg-white disabled:opacity-60">
                {status === "loading" ? "Finding your switch…" : "Find My Switch"} <span aria-hidden>→</span>
              </button>
            </>
          )}

          {status === "error" && (
            <div data-testid="fys-service-error">
              <p className="font-body text-xl font-normal text-white">Find Your Switch is temporarily unavailable. You can still bring us your growth problem directly.</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button type="button" onClick={() => onOpenDiagnostic()} className="font-body border-2 border-[#5f8f10] bg-[#5f8f10] px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] text-black">Start the Diagnostic</button>
                <button type="button" onClick={reset} className="font-body border-2 border-white/35 px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] text-white">Try again</button>
              </div>
            </div>
          )}

          {status === "done" && result && (
            <div data-testid="fys-result">
              <div className="mb-6 border-2 border-white/25 px-4 py-3">
                <p className="font-body text-sm font-semibold uppercase tracking-[0.12em] text-white/65">You told us</p>
                <p className="font-body mt-1 text-base font-normal text-white">{submitted}</p>
              </div>
              <div className="space-y-6">
                <div>
                  <h4 className="font-body text-sm font-semibold uppercase tracking-[0.12em] text-white/65">What this may indicate</h4>
                  <p className="font-body mt-2 text-lg font-normal leading-relaxed text-white">{result.interpretation}</p>
                </div>
                <div>
                  <h4 className="font-body text-sm font-semibold uppercase tracking-[0.12em] text-white/65">The most relevant growth situation</h4>
                  <a href={SITUATION_HREF[result.relevant_situation] || "/what-we-solve"} onClick={() => track("find_switch_result_link_clicked", { target: result.relevant_situation })} className="font-body mt-3 inline-flex items-center gap-2 border-2 border-[#5f8f10] bg-[#5f8f10] px-5 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-black">
                    {result.relevant_situation} <span aria-hidden>→</span>
                  </a>
                </div>
                {result.examine_areas?.length > 0 && (
                  <div>
                    <h4 className="font-body text-sm font-semibold uppercase tracking-[0.12em] text-white/65">What may be worth examining</h4>
                    <ul className="mt-3 space-y-2">
                      {result.examine_areas.map((a, i) => (
                        <li key={i} className="font-body flex items-start gap-3 text-lg font-normal text-white">
                          <span className="mt-2.5 h-2.5 w-2.5 shrink-0 bg-[#5f8f10]" />{a}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {result.recommendations?.length > 0 && (
                  <div>
                    <h4 className="font-body text-sm font-semibold uppercase tracking-[0.12em] text-white/65">Useful SWITCH content</h4>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      {result.recommendations.map((r, i) => (
                        <a key={i} href={r.href || "#"} onClick={() => track("find_switch_result_link_clicked", { target: r.label })} className="border-2 border-white/25 p-4 transition-colors duration-150 hover:border-[#5f8f10]">
                          <p className="font-body text-sm font-semibold uppercase tracking-[0.1em] text-white/65">{r.type}</p>
                          <p className="font-body mt-1 text-base font-normal text-white">{r.label}</p>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4 border-t-2 border-white/20 pt-6">
                <button type="button" data-testid="fys-diagnostic-cta" onClick={() => onOpenDiagnostic(submitted, result.relevant_situation)} className="font-body inline-flex items-center gap-2 border-2 border-[#5f8f10] bg-[#5f8f10] px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] text-black">
                  Bring Us Your Growth Problem <span aria-hidden>→</span>
                </button>
                <button type="button" onClick={reset} className="font-body text-sm font-semibold uppercase tracking-[0.1em] text-white/75 underline underline-offset-4">Start over</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (bare) return inner;
  return (
    <section id="find-your-switch" data-testid="find-your-switch-section" className="border-b-2 border-[#5f8f10] bg-black text-white">
      <div className="mx-auto max-w-[1360px] px-6 py-24 md:px-12 md:py-32 lg:px-16">{inner}</div>
    </section>
  );
};

export default FindYourSwitch;
